import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/api';

function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sliders, setSliders] = useState([]);
  const [slidersLoading, setSlidersLoading] = useState(true);
  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(true);

  const dashboardWrapRef = useRef(null);
  const isMountedRef = useRef(true);

  /** =========================
   *  FETCH TESTIMONIALS
   *  ========================= */
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await api.get('/testimonials');
        const data = response.data;

        if (data.success && Array.isArray(data.data)) {
          setTestimonials(data.data);
        } else {
          setTestimonials([]);
        }
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        setTestimonials([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  /** =========================
   *  FETCH SLIDERS
   *  ========================= */
  useEffect(() => {
    const fetchSliders = async () => {
      try {
        const response = await api.get('/sliders');
        const data = response.data;

        if (data.success && Array.isArray(data.data)) {
          setSliders(data.data);
        } else {
          setSliders([]);
        }
      } catch (error) {
        console.error('Error fetching sliders:', error);
        setSliders([]);
      } finally {
        setSlidersLoading(false);
      }
    };

    fetchSliders();
  }, []);

  /** =========================
   *  FETCH BLOGS (LIMIT 2)
   *  ========================= */
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await api.get('/blogs/recent/2');
        const data = response.data;

        if (data.success && Array.isArray(data.data)) {
          setBlogs(data.data);
        } else {
          setBlogs([]);
        }
      } catch (error) {
        console.error('Error fetching blogs:', error);
        setBlogs([]);
      } finally {
        setBlogsLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Reset mounted ref on mount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /** =========================
   *  INIT HERO CAROUSEL & DASHBOARD SLIDER
   *  Modern React pattern
   *  ========================= */
  useEffect(() => {
    if (!window.jQuery) return;
    const $ = window.jQuery;

    // Bootstrap Hero Carousel
    if ($('#carouselExampleControls').length) {
      try {
        $('#carouselExampleControls').carousel({
          interval: 3000,
          ride: 'carousel',
        });
      } catch (e) {
        console.warn('Bootstrap carousel init error:', e);
      }
    }
  }, []);

  // Dashboard Slick Slider - Initialize when sliders are loaded
  useEffect(() => {
    if (!window.jQuery || slidersLoading || sliders.length === 0) return;
    const $ = window.jQuery;

    // Dashboard Slick Slider
    // Capture ref value to use in cleanup function
    const dashboardElement = dashboardWrapRef.current;
    let dashboardInitialized = false;
    const initDashboardSlider = () => {
      if (!$.fn.slick) {
        console.warn('Slick not loaded for dashboard slider');
        return;
      }

      const $dashboard = $(dashboardElement || '.dashboard-screenshot-wrap');
      if (!$dashboard.length) return;

      if ($dashboard.hasClass('slick-initialized')) {
        $dashboard.slick('unslick');
      }

      try {
        $dashboard.slick({
          dots: false,
          infinite: true,
          autoplay: true,
          autoplaySpeed: 5000,
          arrows: true,
          prevArrow:
            '<button type="button" class="dashboard-prev"><i class="fas fa-arrow-left"></i></button>',
          nextArrow:
            '<button type="button" class="dashboard-next"><i class="fas fa-arrow-right"></i></button>',
          centerMode: true,
          centerPadding: '144px',
          slidesToShow: 1,
          slidesToScroll: 1,
          responsive: [
            {
              breakpoint: 991,
              settings: {
                centerPadding: '50px',
              },
            },
            {
              breakpoint: 575,
              settings: {
                centerPadding: '0',
              },
            },
          ],
        });

        dashboardInitialized = true;
      } catch (e) {
        console.warn('Dashboard slick init error:', e);
      }
    };

    // small delay just to be safe after React render
    const timer = setTimeout(initDashboardSlider, 200);

    return () => {
      clearTimeout(timer);
      if (!$.fn.slick) return;
      
      // Clean up SYNCHRONOUSLY before React tries to remove nodes
      const element = dashboardElement || document.querySelector('.dashboard-screenshot-wrap');
      if (!element || !element.parentNode) return;
      
      try {
        const $dashboard = $(element);
        if (dashboardInitialized && $dashboard.length && $dashboard.hasClass('slick-initialized')) {
          // Destroy slick synchronously - this restores original DOM structure
          $dashboard.slick('unslick');
        }
      } catch (error) {
        // Silently ignore cleanup errors - element may already be removed by React
      }
    };
  }, [sliders, slidersLoading]);


  return (
    <>
      {/* HERO SECTION */}
      <section className="hero-section-three rel z-2 pt-235 rpt-150 pb-130 rpb-100">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 col-md-11">
              <div className="hero-content-three rpt-15 rmb-75">
                <h1 className="mb-15 wow fadeInUp delay-0-2s">
                  We&apos;re Advanced Cloud CRM Software Solutions
                </h1>
                <p className="wow fadeInUp delay-0-4s text-justify">
                  Empower your business with best-in-class CRM software in Malaysia.
                  Our CRM Software (Customer Relationship Management) will help you
                  turn conversations into conversions at every stage of a customer&apos;s
                  journey.
                </p>
                <form
                  className="newsletter-form mt-40"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <div className="newsletter-email wow fadeInUp delay-0-6s">
                    <input type="email" placeholder="Enter Email Address" required />
                    <button type="submit">
                      Get a Free Demo
                      <i className="fas fa-arrow-right" />
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-image-three overlay wow fadeInLeft delay-0-4s">
                <div
                  id="carouselExampleControls"
                  className="carousel slide"
                  data-ride="carousel"
                >
                  <div className="carousel-inner">
                    <div className="carousel-item active">
                      <img className="d-block w-100" src="/my/h1.jpeg" alt="First slide" />
                    </div>
                    <div className="carousel-item">
                      <img className="d-block w-100" src="/my/h2.jpg" alt="Second slide" />
                    </div>
                    <div className="carousel-item">
                      <img className="d-block w-100" src="/my/h3.jpg" alt="Third slide" />
                    </div>
                    <div className="carousel-item">
                      <img className="d-block w-100" src="/my/h4.jpg" alt="Fourth slide" />
                    </div>
                    <div className="carousel-item">
                      <img className="d-block w-100" src="/my/h5.jpg" alt="Fifth slide" />
                    </div>
                  </div>
                  <a
                    className="carousel-control-prev"
                    href="#carouselExampleControls"
                    role="button"
                    data-slide="prev"
                  >
                    <span
                      className="carousel-control-prev-icon"
                      aria-hidden="true"
                    />
                    <span className="sr-only">Previous</span>
                  </a>
                  <a
                    className="carousel-control-next"
                    href="#carouselExampleControls"
                    role="button"
                    data-slide="next"
                  >
                    <span
                      className="carousel-control-next-icon"
                      aria-hidden="true"
                    />
                    <span className="sr-only">Next</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <img
          className="dots-shape"
          src="/assets/images/shapes/dots.png"
          alt="Shape"
        />
        <img
          className="tringle-shape"
          src="/assets/images/shapes/tringle.png"
          alt="Shape"
        />
        <img
          className="close-shape"
          src="/assets/images/shapes/close.png"
          alt="Shape"
        />
      </section>

      {/* SOLUTIONS SECTION */}
      <section
        className="solutions-section-three text-white text-center rel bg-blue pt-130 rpt-100 z-1 pb-75 rpb-45"
        style={{ backgroundImage: 'url(/assets/images/shapes/solutions-bg-dots.png)' }}
      >
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-7 col-lg-8 col-md-10">
              <div className="section-title mb-75">
                <span className="sub-title">Our Solutions</span>
                <h2>All in one Sales CRM for Growing Startups</h2>
              </div>
            </div>
          </div>
          <div className="row justify-content-center">
            <div className="col-xl-4 col-md-6">
              <div className="solution-item-three wow fadeInUp delay-0-2s">
                <i className="far fa-compass" />
                <h3>
                  <Link to="/solutions">Lead Management &amp; Sales Pipeline</Link>
                </h3>
                <p className="text-justify">
                  View each detail about your lead on a single screen with acuIT.
                  Easily add, sort and prioritize high-value deals. Track their
                  activities and change their stages directly without any hassle.
                </p>
              </div>
            </div>
            <div className="col-xl-4 col-md-6">
              <div className="solution-item-three wow fadeInUp delay-0-4s">
                <i className="far fa-map" />
                <h3>
                  <Link to="/solutions">Project Management</Link>
                </h3>
                <p className="text-justify">
                  Prioritize contacts and connect with them on-time with timely follow
                  up reminders. With a graphical timeline view of the contact history,
                  get valuable information that can aid in nurturing the leads.
                </p>
              </div>
            </div>
            <div className="col-xl-4 col-md-6">
              <div className="solution-item-three wow fadeInUp delay-0-6s">
                <i className="fas fa-shield-alt" />
                <h3>
                  <Link to="/solutions">Invoicing &amp; Billing</Link>
                </h3>
                <p className="text-justify">
                  Create valuable reports in seconds and use the information as the
                  base for creating strategies and planning. With acuIT, you can even
                  customize your reports to get the exact information you need for your
                  unique business requirements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section className="about-section-three rel z-1 pt-130 rpt-100">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-xl-7 col-lg-6">
              <div className="about-image rmb-55 wow fadeInLeft delay-0-2s">
                <img src="/assets/images/about/about-two.png" alt="About" />
              </div>
            </div>
            <div className="col-xl-5 col-lg-6">
              <div className="about-content-three wow fadeInRight delay-0-2s">
                <div className="section-title mb-25">
                  <span className="sub-title">Our Special Solutions</span>
                  <h2>Uses of AcuIT CRM</h2>
                </div>
                <ul className="mt-30 mb-45">
                  <li>
                    <i className="fas fa-check" /> Business Management
                  </li>
                  <li>
                    <i className="fas fa-check" /> Predictive Analysis
                  </li>
                  <li>
                    <i className="fas fa-check" /> Sales Tracking
                  </li>
                  <li>
                    <i className="fas fa-check" /> Document Management
                  </li>
                  <li>
                    <i className="fas fa-check" /> Customer Feedback
                  </li>
                  <li>
                    <i className="fas fa-check" /> Targeted Marketing
                  </li>
                  <li>
                    <i className="fas fa-check" /> Customer Tracking
                  </li>
                  <li>
                    <i className="fas fa-check" /> Task Management
                  </li>
                  <li>
                    <i className="fas fa-check" /> Mobility
                  </li>
                  <li>
                    <i className="fas fa-check" /> Data Analysis
                  </li>
                </ul>
                <Link to="/contact" className="theme-btn style-three">
                  Get Started <i className="fas fa-arrow-right" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SUPPORT SECTION */}
      <section className="browswr-support-section rel z-1 py-130 rpy-100">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-xl-5 col-lg-6">
              <div className="browswr-support-content rmb-55 wow fadeInRight delay-0-2s">
                <div className="section-title">
                  <span className="sub-title">Our Support</span>
                </div>
                <div className="row">
                  <div className="col-md-12">
                    <div className="solution-item-two">
                      <h4>Customer Interaction Management</h4>
                      <p className="text-justify">
                        <i className="fas fa-check" /> When customers choose to engage
                        with a company or brand, they have high expectations. Demands
                        and requests must be prioritized, and problems must be resolved
                        at the earliest.
                      </p>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="solution-item-two color-two">
                      <h4>To improve brand loyalty</h4>
                      <p className="text-justify">
                        <i className="fas fa-check" /> The only way to transform
                        customers into brand loyalists is for them to have a positive
                        experience with you. It is important to stay in the good books
                        of your customers at all times.
                      </p>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="solution-item-two color-two">
                      <h4>To stimulate a community of engaged users</h4>
                      <p className="text-justify">
                        <i className="fas fa-check" /> Customers can access community
                        forums to ask questions, propose ideas, discuss their
                        experiences, and, most importantly, communicate with other
                        users. Customers will be able to assist one another instead of
                        contacting your support team for minor issues.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-7 col-lg-6">
              <div className="browswr-support-image text-lg-right wow fadeInLeft delay-0-2s">
                <img
                  src="/assets/images/about/borwser-support.jpg"
                  alt="Browser Support"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER SECTION */}
      <section className="newsletter-section-two rel z-2">
        <div className="container">
          <div className="newsletter-inner style-two bg-gray bgs-cover text-white rel z-1">
            <div className="row align-items-center align-items-xl-start">
              <div className="col-lg-6">
                <div className="newsletter-content p-60 wow fadeInUp delay-0-2s">
                  <div className="section-title mb-30">
                    <span className="sub-title">Our Newsletter</span>
                    <h2>Subscribe Our Newsletter to Get More Updates</h2>
                  </div>
                  <form
                    className="newsletter-form"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <div className="newsletter-email">
                      <input type="email" placeholder="Enter Email Address" required />
                      <button type="submit">
                        Sign up <i className="fas fa-angle-right" />
                      </button>
                    </div>
                    <div className="newsletter-radios">
                      <div className="custom-control custom-radio">
                        <input
                          type="radio"
                          className="custom-control-input"
                          id="wekly"
                          name="example1"
                          defaultChecked
                        />
                        <label className="custom-control-label" htmlFor="wekly">
                          Weekly Updates
                        </label>
                      </div>
                      <div className="custom-control custom-radio">
                        <input
                          type="radio"
                          className="custom-control-input"
                          id="monthly"
                          name="example1"
                        />
                        <label className="custom-control-label" htmlFor="monthly">
                          Monthly Updates
                        </label>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="newsletter-images wow fadeInUp delay-0-4s">
                  <img
                    src="/assets/images/newsletter/newsletter-two.png"
                    alt="Newsletter"
                  />
                  <img
                    src="/assets/images/newsletter/circle.png"
                    alt="shape"
                    className="circle slideUpRight"
                  />
                  <img
                    src="/assets/images/newsletter/dots.png"
                    alt="shape"
                    className="dots slideLeftRight"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section className="services-section-three bg-lighter rel z-1 pt-250 pb-100 rpb-70">
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-xl-7 col-lg-8 col-md-10">
              <div className="section-title mt-100 rmt-70 mb-55">
                <span className="sub-title">Our Special Solutions</span>
                <h2>We Customize Your Software to The Smart Ways</h2>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-xl-4 col-md-6">
              <div className="service-item wow fadeInUp delay-0-2s">
                <i className="flaticon-file" />
                <div className="content">
                  <h3>
                    <Link to="/solutions">Consulting</Link>
                  </h3>
                  <p>
                    Fast track your business cycle with tailored CRM software in
                    Malaysia and strategy to keep pace with the current needs.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-xl-4 col-md-6">
              <div className="service-item wow fadeInUp delay-0-4s">
                <i className="flaticon-responsive-design" />
                <div className="content">
                  <h3>
                    <Link to="/solutions">Value for Money</Link>
                  </h3>
                  <p>
                    Save at least 50% of CRM cost compared to other CRM solutions.
                    Complete yet affordable CRM
                  </p>
                </div>
              </div>
            </div>
            <div className="col-xl-4 col-md-6">
              <div className="service-item wow fadeInUp delay-0-6s">
                <i className="flaticon-security" />
                <div className="content">
                  <h3>
                    <Link to="/solutions">Excellent Support</Link>
                  </h3>
                  <p>
                    We provide best customer service over phone &amp; remote tools to
                    ensure your CRM success
                  </p>
                </div>
              </div>
            </div>
            <div className="col-xl-4 col-md-6">
              <div className="service-item wow fadeInUp delay-0-8s">
                <i className="flaticon-puzzle" />
                <div className="content">
                  <h3>
                    <Link to="/solutions">Fast &amp; Secure</Link>
                  </h3>
                  <p>
                    Our datacenters are hosted in some of the most secure facilities
                    available that are protected from physical and logical attacks as
                    well as from natural disasters
                  </p>
                </div>
              </div>
            </div>
            <div className="col-xl-4 col-md-6">
              <div className="service-item wow fadeInUp delay-1-0s">
                <i className="flaticon-badge" />
                <div className="content" style={{ height: '255px' }}>
                  <h3>
                    <Link to="/solutions">Ready to Use</Link>
                  </h3>
                  <p>
                    AcuIT CRM is ready to use with all fields, forms and reports are
                    pre-configured for small and medium businesses
                  </p>
                </div>
              </div>
            </div>
            <div className="col-xl-4 col-md-6">
              <div className="service-item wow fadeInUp delay-1-2s">
                <i className="flaticon-analytics" />
                <div className="content" style={{ height: '255px' }}>
                  <h3>
                    <Link to="/solutions">No Hidden Cost</Link>
                  </h3>
                  <p>
                    There are no additional surprise costs. What you pay for is what
                    you get. The pricing is flexible as well
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DASHBOARD SCREENSHOTS */}
      <section className="dashboard-section rel z-1 py-130 rpy-100">
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-xl-7 col-lg-8 col-md-10">
              <div className="section-title mb-60">
                <span className="sub-title">Dashboard Screenshot</span>
                <h2>Let&apos;s See Awesome Software UI Design Screenshot</h2>
              </div>
            </div>
          </div>
          {slidersLoading ? (
            <div className="text-center py-5">
              <p>Loading dashboard screenshots...</p>
            </div>
          ) : sliders.length > 0 ? (
            <div
              className="dashboard-screenshot-wrap"
              ref={dashboardWrapRef}
              suppressHydrationWarning
              key="dashboard-slider"
            >
              {sliders.map((slider) => (
                <div key={slider.id} className="dashboard-screenshot-item" data-slick-slide="true">
                  <img 
                    src={slider.image || '/my/h1.jpeg'} 
                    alt="Dashboard Screenshot" 
                    onError={(e) => {
                      e.target.src = '/my/h1.jpeg';
                    }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <p>No dashboard screenshots available.</p>
            </div>
          )}
        </div>
      </section>

      {/* TESTIMONIALS SECTION - NEW DESIGN */}
      <section className="testimonials-section-new py-120 rpy-100">
        <div className="container">
          <div className="testimonials-header text-center">
            <p className="testimonials-subtitle">
              {testimonials.length > 0 
                ? `${testimonials.length} people have said how good AcuIT.`
                : 'Our happy clients say about us'}
            </p>
            <h2 className="testimonials-main-title">
              Our happy clients say about us
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <p>Loading testimonials...</p>
            </div>
          ) : testimonials.length > 0 ? (
            <>
              <div className="testimonials-grid-wrap">
                {testimonials.slice(0, 3).map((testimonial, index) => {
                  if (!testimonial || !testimonial.id) return null;

                  const uniqueKey = `testimonial-${testimonial.id}-${index}`;
                  const rating = testimonial.rating || 5;
                  const authorTitle = testimonial.designation 
                    ? `${testimonial.designation}${testimonial.company ? ` at ${testimonial.company}` : ''}`
                    : testimonial.company || '';

                  return (
                    <div key={uniqueKey} className="testimonial-card-new">
                      {/* Review Text */}
                      <div className="testimonial-review-text">
                        <p>
                          {testimonial.testimonial
                            ? testimonial.testimonial.replace(/&nbsp;/g, ' ')
                            : ''}
                        </p>
                      </div>

                      {/* Rating Section */}
                      <div className="testimonial-rating-new">
                        <div className="rating-stars">
                          {Array.from({ length: 5 }).map((_, starIndex) => {
                            const starNumber = starIndex + 1;
                            const isFilled = starNumber <= rating;
                            return (
                              <i
                                key={starIndex}
                                className={`fas fa-star ${
                                  isFilled ? 'star-filled' : 'star-empty'
                                }`}
                              />
                            );
                          })}
                        </div>
                        <span className="rating-badge">{rating}/5</span>
                      </div>

                      {/* Author Section */}
                      <div className="testimonial-author-new">
                        <div className="author-avatar">
                          <img
                            src={testimonial.image || '/assets/images/team/default-avatar.svg'}
                            alt={testimonial.name || 'Author'}
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"%3E%3Ccircle cx="24" cy="24" r="24" fill="%23E5E7EB"/%3E%3Cpath d="M24 24c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8zm0 4c-5.3 0-16 2.7-16 8v4h32v-4c0-5.3-10.7-8-16-8z" fill="%23999"/%3E%3C/svg%3E';
                            }}
                          />
                        </div>
                        <div className="author-info">
                          <h3 className="author-name">
                            {testimonial.name || 'Unknown'}
                          </h3>
                          <p className="author-title">{authorTitle}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {testimonials.length > 3 && (
                <div className="testimonials-footer text-center">
                  <Link to="/testimonials" className="btn-check-reviews" style={{ textDecoration: 'none', display: 'inline-block' }}>
                    Check all {testimonials.length} reviews
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-5">
              <p>No testimonials available at the moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* BLOG SECTION */}
      <section className="blog-section-two bg-lighter rel z-1 pt-130 rpt-100 pb-180 rpb-150">
        <div className="container">
          <div className="row">
            <div className="col-xl-4 col-lg-8 col-md-10">
              <div className="blog-two-section-content mb-55 wow fadeInUp delay-0-2s">
                <div className="section-title mb-25">
                  <span className="sub-title">Latest News &amp; Blog</span>
                  <h2>Get Our Every Single Update Latest News and Blog</h2>
                </div>
              </div>
            </div>
            {blogsLoading ? (
              <div className="col-xl-8">
                <div className="text-center py-5">
                  <p>Loading blogs...</p>
                </div>
              </div>
            ) : blogs.length > 0 ? (
              <>
                {blogs.map((blog, index) => (
                  <div key={blog.id || index} className="col-xl-4 col-md-6">
                    <div
                      className="blog-item-two wow fadeInUp delay-0-4s"
                      style={{ height: '450px' }}
                    >
                      <div
                        className="blog-two-image"
                        style={{
                          backgroundImage: `url(${blog.featured_image || '/my/cb1.png'})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      />
                      <h3>
                        <Link to={`/blog/${blog.id || blog.slug || '#'}`}>
                          {blog.title || 'Blog Title'}
                        </Link>
                      </h3>
                      <p>
                        {blog.short_description 
                          ? blog.short_description.length > 150 
                            ? blog.short_description.substring(0, 150) + '...'
                            : blog.short_description
                          : 'Read our latest blog post to learn more about our services and solutions.'}
                      </p>
                      <Link 
                        to={`/blog/${blog.id || blog.slug || '#'}`} 
                        className="read-more"
                      >
                        Read More <i className="fas fa-arrow-right" />
                      </Link>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="col-xl-8">
                <div className="text-center py-5">
                  <p>No blog posts available at the moment.</p>
                </div>
              </div>
            )}
          </div>
          {/* View More Button */}
          <div className="row mt-40">
            <div className="col-12 text-center">
              <Link 
                to="/blog" 
                className="theme-btn"
                style={{
                  display: 'inline-block',
                  textDecoration: 'none'
                }}
              >
                View More <i className="fas fa-arrow-right" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;

